import React, { useState, useEffect } from 'react'
import { Award, Trophy, Star, Calendar, MapPin } from 'lucide-react'

const Awards = () => {
  const [selectedYear, setSelectedYear] = useState('all')
  const [awards, setAwards] = useState([])

  const years = ['all', '2024', '2023', '2022', '2021']

  const awardTypes = {
    'winner': { icon: Trophy, color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Winner' },
    'nominated': { icon: Star, color: 'bg-blue-100 text-blue-800 border-blue-300', label: 'Nominated' },
    'official-selection': { icon: Award, color: 'bg-purple-100 text-purple-800 border-purple-300', label: 'Official Selection' }
  }

  // Sample awards data
  const sampleAwards = [
    {
      id: 1,
      title: 'Best Drama Series',
      project: 'Shadows of Dhaka',
      festival: 'Bangladesh International Film Festival',
      year: 2024,
      location: 'Dhaka, Bangladesh',
      type: 'winner',
      description: 'Won Best Drama Series for exceptional storytelling and cultural representation'
    },
    {
      id: 2,
      title: 'Best Short Film',
      project: 'The River\'s Song',
      festival: 'South Asian Film Festival',
      year: 2024,
      location: 'Mumbai, India',
      type: 'official-selection',
      description: 'Official selection showcasing poetic cinematography and authentic Bengali culture'
    },
    {
      id: 3,
      title: 'Best Cinematography',
      project: 'Eternal Bonds',
      festival: 'Asian Film Awards',
      year: 2023,
      location: 'Singapore',
      type: 'winner',
      description: 'Awarded for outstanding visual storytelling and technical excellence'
    },
    {
      id: 4,
      title: 'Best Music Video',
      project: 'Monsoon Melodies',
      festival: 'International Music Video Festival',
      year: 2023,
      location: 'London, UK',
      type: 'nominated',
      description: 'Nominated for innovative direction and cultural authenticity'
    },
    {
      id: 5,
      title: 'Best Documentary',
      project: 'Voices of Heritage',
      festival: 'Documentary Film Festival Asia',
      year: 2024,
      location: 'Bangkok, Thailand',
      type: 'winner',
      description: 'Won for preserving and showcasing traditional Bangladeshi art forms'
    },
    {
      id: 6,
      title: 'Audience Choice Award',
      project: 'City Lights',
      festival: 'Dhaka Film Festival',
      year: 2023,
      location: 'Dhaka, Bangladesh',
      type: 'winner',
      description: 'Popular choice for contemporary storytelling and relatable characters'
    }
  ]

  useEffect(() => {
    const savedAwards = localStorage.getItem('shikdar_films_awards')
    if (savedAwards) {
      setAwards(JSON.parse(savedAwards))
    } else {
      setAwards(sampleAwards)
      localStorage.setItem('shikdar_films_awards', JSON.stringify(sampleAwards))
    }
  }, [])

  const filteredAwards = selectedYear === 'all' 
    ? awards 
    : awards.filter(award => award.year.toString() === selectedYear)

  return (
    <div className="min-h-screen bg-gray-50 py-16" data-component-path="client/src/pages/Awards.jsx" data-component-line="95:4" data-component-id="client/src/pages/Awards.jsx:95:4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-component-path="client/src/pages/Awards.jsx" data-component-line="96:6" data-component-id="client/src/pages/Awards.jsx:96:6">
        {/* Header */}
        <div className="text-center mb-12" data-component-path="client/src/pages/Awards.jsx" data-component-line="98:8" data-component-id="client/src/pages/Awards.jsx:98:8">
          <div className="flex items-center justify-center gap-3 mb-4" data-component-path="client/src/pages/Awards.jsx" data-component-line="99:10" data-component-id="client/src/pages/Awards.jsx:99:10">
            <Trophy className="w-12 h-12 text-bengal-gold" data-component-path="client/src/pages/Awards.jsx" data-component-line="100:12" data-component-id="client/src/pages/Awards.jsx:100:12" />
            <h1 className="section-title text-4xl md:text-5xl text-gray-900" data-component-path="client/src/pages/Awards.jsx" data-component-line="101:12" data-component-id="client/src/pages/Awards.jsx:101:12">
              Awards & Recognition
            </h1>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-component-path="client/src/pages/Awards.jsx" data-component-line="105:10" data-component-id="client/src/pages/Awards.jsx:105:10">
            Celebrating our achievements at national and international film festivals
          </p>
        </div>

        {/* Year Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-component-path="client/src/pages/Awards.jsx" data-component-line="111:8" data-component-id="client/src/pages/Awards.jsx:111:8">
          {years.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(year)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                selectedYear === year
                  ? 'bg-bengal-gold text-white shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
              data-component-path="client/src/pages/Awards.jsx" data-component-line="113:12" data-component-id="client/src/pages/Awards.jsx:113:12"
            >
              {year === 'all' ? 'All Years' : year}
            </button>
          ))}
        </div>

        {/* Awards Grid */}
        {filteredAwards.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-component-path="client/src/pages/Awards.jsx" data-component-line="128:10" data-component-id="client/src/pages/Awards.jsx:128:10">
            {filteredAwards.map((award) => {
              const AwardIcon = awardTypes[award.type].icon
              return (
                <div 
                  key={award.id}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-gray-100 hover:border-bengal-gold"
                  data-component-path="client/src/pages/Awards.jsx" data-component-line="132:16" data-component-id="client/src/pages/Awards.jsx:132:16"
                >
                  <div className="p-6" data-component-path="client/src/pages/Awards.jsx" data-component-line="137:18" data-component-id="client/src/pages/Awards.jsx:137:18">
                    {/* Award Type Badge */}
                    <div className="flex items-center justify-between mb-4" data-component-path="client/src/pages/Awards.jsx" data-component-line="139:20" data-component-id="client/src/pages/Awards.jsx:139:20">
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border-2 ${awardTypes[award.type].color}`} data-component-path="client/src/pages/Awards.jsx" data-component-line="140:22" data-component-id="client/src/pages/Awards.jsx:140:22">
                        <AwardIcon className="w-4 h-4" data-component-path="client/src/pages/Awards.jsx" data-component-line="141:24" data-component-id="client/src/pages/Awards.jsx:141:24" />
                        {awardTypes[award.type].label}
                      </span>
                      <div className="flex items-center gap-2 text-gray-500" data-component-path="client/src/pages/Awards.jsx" data-component-line="144:22" data-component-id="client/src/pages/Awards.jsx:144:22">
                        <Calendar className="w-4 h-4" data-component-path="client/src/pages/Awards.jsx" data-component-line="145:24" data-component-id="client/src/pages/Awards.jsx:145:24" />
                        <span className="text-sm font-medium" data-component-path="client/src/pages/Awards.jsx" data-component-line="146:24" data-component-id="client/src/pages/Awards.jsx:146:24">{award.year}</span>
                      </div>
                    </div>

                    {/* Award Title */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2 section-title" data-component-path="client/src/pages/Awards.jsx" data-component-line="151:20" data-component-id="client/src/pages/Awards.jsx:151:20">
                      {award.title}
                    </h3>

                    {/* Project Name */}
                    <p className="text-bengal-green font-semibold mb-3" data-component-path="client/src/pages/Awards.jsx" data-component-line="156:20" data-component-id="client/src/pages/Awards.jsx:156:20">
                      {award.project}
                    </p>

                    {/* Festival Info */}
                    <div className="space-y-2 mb-4" data-component-path="client/src/pages/Awards.jsx" data-component-line="161:20" data-component-id="client/src/pages/Awards.jsx:161:20">
                      <p className="text-gray-700 font-medium" data-component-path="client/src/pages/Awards.jsx" data-component-line="162:22" data-component-id="client/src/pages/Awards.jsx:162:22">{award.festival}</p>
                      <div className="flex items-center gap-2 text-gray-600" data-component-path="client/src/pages/Awards.jsx" data-component-line="163:22" data-component-id="client/src/pages/Awards.jsx:163:22">
                        <MapPin className="w-4 h-4 flex-shrink-0" data-component-path="client/src/pages/Awards.jsx" data-component-line="164:24" data-component-id="client/src/pages/Awards.jsx:164:24" />
                        <span className="text-sm" data-component-path="client/src/pages/Awards.jsx" data-component-line="165:24" data-component-id="client/src/pages/Awards.jsx:165:24">{award.location}</span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-600 text-sm leading-relaxed" data-component-path="client/src/pages/Awards.jsx" data-component-line="170:20" data-component-id="client/src/pages/Awards.jsx:170:20">
                      {award.description}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-16" data-component-path="client/src/pages/Awards.jsx" data-component-line="179:10" data-component-id="client/src/pages/Awards.jsx:179:10">
            <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" data-component-path="client/src/pages/Awards.jsx" data-component-line="180:12" data-component-id="client/src/pages/Awards.jsx:180:12" />
            <p className="text-gray-500 text-lg" data-component-path="client/src/pages/Awards.jsx" data-component-line="181:12" data-component-id="client/src/pages/Awards.jsx:181:12">No awards found for this year</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default Awards